'use client';

import React from 'react';
import { Users, Clock, DollarSign, TrendingUp, AlertTriangle, Minus } from 'lucide-react';
import type { PayPeriod } from '../page';
import Icon from '@/components/ui/AppIcon';


interface PayrollSummaryCardsProps {
  period: PayPeriod;
}

export default function PayrollSummaryCards({ period }: PayrollSummaryCardsProps) {
  const avgHoursPerStaff = period.totalStaff > 0 ? Math.round(period.totalHours / period.totalStaff) : 0;
  const avgNetPay = period.totalStaff > 0 ? Math.round(period.totalNet / period.totalStaff) : 0;
  const deductionRate = period.totalGross > 0 ? ((period.totalDeductions / period.totalGross) * 100).toFixed(1) : '0';
  const otPct = period.totalHours > 0 ? ((period.overtimeHours / period.totalHours) * 100).toFixed(1) : '0';

  const cards = [
    {
      label: 'Total Staff Paid',
      value: period.totalStaff,
      sub: `Avg ${avgHoursPerStaff} hrs/staff`,
      icon: Users,
      color: 'text-info',
      bg: 'bg-info/10',
    },
    {
      label: 'Total Hours Worked',
      value: period.totalHours.toLocaleString(),
      sub: `${period.overtimeHours} overtime hrs (${otPct}%)`,
      icon: Clock,
      color: 'text-warning',
      bg: 'bg-warning/10',
    },
    {
      label: 'Total Gross Pay',
      value: `$${period.totalGross.toLocaleString()}`,
      sub: `Avg $${avgNetPay.toLocaleString()} net/staff`,
      icon: DollarSign,
      color: 'text-positive',
      bg: 'bg-positive/10',
    },
    {
      label: 'Total Deductions',
      value: `$${period.totalDeductions.toLocaleString()}`,
      sub: `${deductionRate}% of gross pay`,
      icon: Minus,
      color: 'text-danger',
      bg: 'bg-danger/10',
    },
    {
      label: 'Total Net Pay',
      value: `$${period.totalNet.toLocaleString()}`,
      sub: 'After all deductions',
      icon: TrendingUp,
      color: 'text-primary',
      bg: 'bg-primary/10',
    },
    {
      label: 'Overtime Cost',
      value: `$${period.overtimeCost.toLocaleString()}`,
      sub: `${period.overtimeHours} OT hours billed`,
      icon: AlertTriangle,
      color: 'text-warning',
      bg: 'bg-warning/10',
    },
  ];

  return (
    <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3">
      {cards.map((card) => {
        const Icon = card.icon;
        return (
          <div key={card.label} className="bg-card border border-border rounded-xl p-4 flex flex-col gap-2">
            <div className={`w-8 h-8 rounded-lg ${card.bg} flex items-center justify-center`}>
              <Icon size={16} className={card.color} />
            </div>
            <div>
              <p className="text-[11px] text-muted-foreground font-500 leading-tight">{card.label}</p>
              <p className="text-lg font-700 text-foreground tabular-nums mt-0.5">{card.value}</p>
              <p className="text-[11px] text-muted-foreground mt-0.5">{card.sub}</p>
            </div>
          </div>
        );
      })}
    </div>
  );
}
